import { listTags, updateTalentMember } from "@fp/admin-api-client/client";
import type { TalentMemberDto } from "@fp/admin-api-client/types/talentMemberDto.js";
import type { TalentTagDto } from "@fp/admin-api-client/types/talentTagDto.js";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { createContext, useContext, useMemo, useState } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/base/button-group/button-group";
import { Button } from "@/components/base/buttons/button";
import { FormPhoneField } from "@/components/base/form/form-phone-field";
import { FormTextField } from "@/components/base/form/form-text-field";
import { FormTextAreaField } from "@/components/base/form/form-textarea-field";
import {
	Form,
	FormField,
	type UseHookFormProps,
	useHookForm,
} from "@/components/base/form/hook-form";
import { cx } from "@/utils/cx";
import { Drawer } from "../application/drawer/drawer";
import { FilterTag } from "../application/filter-tag/filter-tag";
import { TagSelector } from "../application/tag-selector/tag-selector";
import { FloatingAddButton } from "../base/buttons/floating-add-button";
import { AddressFieldGroup } from "../common/address-field-group";

export const TalentMemberFormSchema = z.object({
	id: z.string(),
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().optional().or(z.literal("")),
	email: z.string().email("Invalid email address").optional().or(z.literal("")),
	phone: z.string().optional().or(z.literal("")),
	type: z.enum(["w2", "1099", "unclassified"]).optional(),
	bio: z.string().optional().or(z.literal("")),
	addressLine1: z.string().optional().or(z.literal("")),
	addressLine2: z.string().optional().or(z.literal("")),
	city: z.string().optional().or(z.literal("")),
	state: z.string().optional().or(z.literal("")),
	zip: z.string().optional().or(z.literal("")),
	tags: z.array(z.string()),
});

export type TalentMemberFormSchema = z.infer<typeof TalentMemberFormSchema>;

export type TalentMemberFormContext = {
	form: UseFormReturn<TalentMemberFormSchema>;
	handleSubmit: () => void;
	transition: boolean;
	errorMessage: string | undefined;
};

export const TalentMemberFormContext = createContext<TalentMemberFormContext | undefined>(
	undefined,
);

export const useTalentMemberForm = () => {
	const context = useContext(TalentMemberFormContext);
	if (!context) {
		throw new Error("TalentMemberFormContext not found");
	}
	return context;
};

export type TalentMemberFormProps = UseHookFormProps<
	TalentMemberFormSchema,
	TalentMemberDto
> & {
	talentMember: TalentMemberDto;
	children:
		| ((context: TalentMemberFormContext) => React.ReactNode)
		| React.ReactNode;
};

export const TalentMemberFormSubmitHandler = async (
	formValues: TalentMemberFormSchema,
) => {
	try {
		const addressFields = {
			addressLine1: formValues.addressLine1 || undefined,
			addressLine2: formValues.addressLine2 || undefined,
			city: formValues.city || undefined,
			state: formValues.state || undefined,
			zip: formValues.zip || undefined,
		};
		const hasAddress = Object.values(addressFields).some(Boolean);

		const updateData = {
			firstName: formValues.firstName,
			lastName: formValues.lastName || undefined,
			email: formValues.email || undefined,
			phone: formValues.phone || undefined,
			type: formValues.type,
			bio: formValues.bio || undefined,
			address: hasAddress ? addressFields : null,
			tags: formValues.tags,
			isActive: true,
		} as Parameters<typeof updateTalentMember>[1];

		const result = await updateTalentMember(formValues.id, updateData);
		if (
			result.status !== HttpStatusCode.Ok &&
			result.status !== HttpStatusCode.Created
		) {
			return {
				success: false as const,
				error: new Error("Unable to update talentMember"),
			};
		}
		return { success: true as const, data: result.data };
	} catch (error) {
		const message =
			(error as { response?: { data?: { message?: string } } })?.response?.data
				?.message ?? "Unable to save talentMember";
		return {
			success: false as const,
			error: new Error(message),
		};
	}
};

export const TalentMemberForm = ({
	talentMember,
	children,
	onSubmit = TalentMemberFormSubmitHandler,
	...props
}: TalentMemberFormProps) => {
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);

	const defaultValues = useMemo(
		() => ({
			id: talentMember.id,
			firstName: talentMember.firstName || "",
			lastName: talentMember.lastName || "",
			email: talentMember.email || "",
			phone: talentMember.phone || "",
			type: (talentMember.type || "unclassified") as
				| "w2"
				| "1099"
				| "unclassified"
				| undefined,
			bio: talentMember.bio || "",
			addressLine1: talentMember.address?.addressLine1 || "",
			addressLine2: talentMember.address?.addressLine2 || "",
			city: talentMember.address?.city || "",
			state: talentMember.address?.state || "",
			zip: talentMember.address?.zip || "",
			tags: (talentMember.tags?.map((t) => t.id).filter(Boolean) as string[]) ?? [],
		}),
		[talentMember],
	);

	const { form, handleSubmit, transition } = useHookForm<
		TalentMemberFormSchema,
		TalentMemberDto
	>({
		resolver: zodResolver(TalentMemberFormSchema),
		defaultValues,
		onSuccess: async (data) => {
			setErrorMessage(undefined);
			await props.onSuccess?.(data);
		},
		onError: async (error) => {
			setErrorMessage(
				error instanceof Error ? error.message : "Unable to save talentMember",
			);
			await props.onError?.(error);
		},
		onSubmit,
		...props,
	});

	const context = useMemo(
		() => ({ form, handleSubmit, transition, errorMessage }),
		[form, handleSubmit, transition, errorMessage],
	);

	return (
		<TalentMemberFormContext.Provider value={context}>
			<Form {...form}>
				{typeof children === "function" ? children(context) : children}
			</Form>
		</TalentMemberFormContext.Provider>
	);
};

export const TalentMemberFormErrorMessage = ({
	className,
}: {
	className?: string;
}) => {
	const { errorMessage } = useTalentMemberForm();

	if (!errorMessage) return null;

	return <p className={cx("text-error-primary", className)}>{errorMessage}</p>;
};

export const TalentMemberFormContent = ({ className }: { className?: string }) => {
	const { form } = useTalentMemberForm();
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

	const onSelectedTagsChange = (tagIds: string[]) => {
		form.setValue("tags", tagIds);
	};

	const { data: allTags, isLoading: areTagsLoading } = useQuery({
		queryKey: ["tags"],
		queryFn: async () => {
			const response = await listTags(undefined);
			if (response.status !== HttpStatusCode.Ok) {
				throw new Error("Failed to load tags");
			}
			return response.data.items ?? [];
		},
	});

	const tags = useWatch({ control: form.control, name: "tags" });
	const addressLine1Watch = useWatch({ control: form.control, name: "addressLine1" });
	const addressLine2Watch = useWatch({ control: form.control, name: "addressLine2" });
	const cityWatch = useWatch({ control: form.control, name: "city" });
	const stateWatch = useWatch({ control: form.control, name: "state" });
	const zipWatch = useWatch({ control: form.control, name: "zip" });

	return (
		<div className={cx("flex flex-col gap-6 mb-4", className)}>
			<div className="flex flex-col gap-4">
				<div className="flex gap-4">
					<FormField
						control={form.control}
						name="firstName"
						render={({ field }) => (
							<FormTextField
								field={field}
								label="First Name"
								placeholder=""
								className="flex-1"
							/>
						)}
					/>
					<FormField
						control={form.control}
						name="lastName"
						render={({ field }) => (
							<FormTextField
								field={field}
								label="Last Name"
								placeholder=""
								className="flex-1"
							/>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormTextField
							field={field}
							label="Email"
							placeholder=""
							className="w-full"
							type="email"
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="phone"
					render={({ field }) => (
						<FormPhoneField
							field={field}
							label="Phone Number"
							placeholder=""
							className="w-full"
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="type"
					render={({ field }) => (
						<div className="space-y-2">
							<p className="font-semibold text-gray-500">Employment Status</p>
							<ButtonGroup
								selectedKeys={field.value ? [field.value] : []}
								onSelectionChange={(keys) => {
									const selectedKey = Array.from(keys)[0] as string;
									field.onChange(
										selectedKey === field.value ? undefined : selectedKey,
									);
								}}
							>
								<ButtonGroupItem id="w2">W2</ButtonGroupItem>
								<ButtonGroupItem id="1099">1099</ButtonGroupItem>
								<ButtonGroupItem id="unclassified">
									Unclassified
								</ButtonGroupItem>
							</ButtonGroup>
						</div>
					)}
				/>
			</div>

			<FormField
				control={form.control}
				name="bio"
				render={({ field }) => (
					<FormTextAreaField
						field={field}
						label="Bio"
						placeholder=""
						className="w-full"
					/>
				)}
			/>

			<div className="flex flex-col gap-4">
				<p className="font-semibold text-gray-500">Address</p>
				<AddressFieldGroup
					value={{
						addressLine1: addressLine1Watch,
						addressLine2: addressLine2Watch,
						city: cityWatch,
						state: stateWatch,
						zip: zipWatch,
					}}
					onChange={(v) => {
						form.setValue("addressLine1", v.addressLine1 ?? "");
						form.setValue("addressLine2", v.addressLine2 ?? "");
						form.setValue("city", v.city ?? "");
						form.setValue("state", v.state ?? "");
						form.setValue("zip", v.zip ?? "");
					}}
				/>
			</div>

			<FormField
				control={form.control}
				name="tags"
				render={({ field }) => (
					<div className="space-y-3">
						<div className="flex justify-between">
							<p className="font-semibold text-gray-500">
								Tags ({field.value?.length ?? 0})
							</p>
							<FloatingAddButton onClick={() => setIsFilterDrawerOpen(true)} />
						</div>

						{areTagsLoading ? (
							<div className="text-sm text-gray-500">Loading tags...</div>
						) : (
							<div className="flex flex-wrap gap-2">
								{allTags
									?.filter((tag) => field.value?.includes(tag.id ?? ""))
									.map((tag: TalentTagDto) => (
										<FilterTag
											key={tag.id ?? ""}
											tag={tag}
											selected={false}
											onDelete={(tag) => {
												form.setValue(
													"tags",
													field.value.filter((tid) => tid !== tag.id),
												);
											}}
										/>
									))}
							</div>
						)}
					</div>
				)}
			/>

			<Drawer
				isOpen={isFilterDrawerOpen}
				onClose={setIsFilterDrawerOpen}
				title="Filter by tag"
			>
				<TagSelector
					selectedTags={tags || []}
					onChange={onSelectedTagsChange}
				/>

				<Button
					className="mt-6 w-full"
					size="lg"
					onClick={() => {
						setIsFilterDrawerOpen(false);
					}}
				>
					Done
				</Button>
			</Drawer>
		</div>
	);
};

export const TalentMemberFormActions = ({ className }: { className?: string }) => {
	const { handleSubmit, transition } = useTalentMemberForm();

	return (
		<div className={cx("flex flex-col gap-4 px-6", className)}>
			<Button
				type="submit"
				size="lg"
				color="primary"
				isDisabled={transition}
				onClick={handleSubmit}
			>
				Save
			</Button>
		</div>
	);
};
